// Cart line identity.
//
// A line is identified by what is being sold AND the unit it is being sold in —
// never by the product alone. A product configured with Piece / Box / Dozen must
// be able to sit on one invoice three times, once per unit, because each of those
// is a different thing to sell at a different price that draws a different amount
// of stock.
//
// Every add-to-cart path used to re-implement this match inline and they disagreed:
// the product tile keyed on prodId + prodType, the variant dialog on varProdId, and
// only the search/scan path included the unit. So a second unit could reach the cart
// only by scanning that unit's own barcode. This module is the single rule.
//
// prodType: 1 = standard, 2 = variant, 3 = composite, 4 = service.

export const PROD_TYPE_SERVICE = 4;

/**
 * Build a comparable identity from any source shape.
 *
 * Call sites map their own object in, because the shapes genuinely differ — the
 * product tile carries `val.id`, the search box carries `val.prodId`, and a cart
 * line carries its base unit nested at `prod.baseUnitId`.
 *
 * `unitId: null` means "the product's base unit", so it is normalised to the
 * explicit base unit id. Without that, a line parked before this feature (null)
 * would refuse to merge with a fresh tile add (which seeds baseUnitId), and the
 * cashier would see two identical Piece lines.
 */
export const cartLineKey = (src) => ({
  prodType: src?.prodType ?? null,
  prodId: src?.prodId ?? null,
  varProdId: src?.varProdId ?? null,
  serviceId: src?.serviceId ?? null,
  unitId: src?.unitId || src?.baseUnitId || null,
});

/** Identity of a line already in the cart. */
export const lineKeyOf = (line) =>
  cartLineKey({
    prodType: line?.prodType,
    prodId: line?.prodId,
    varProdId: line?.varProdId,
    serviceId: line?.serviceId,
    unitId: line?.unitId,
    baseUnitId: line?.prod?.baseUnitId,
  });

export const sameCartLine = (a, b) =>
  !!a &&
  !!b &&
  a.prodType === b.prodType &&
  a.prodId === b.prodId &&
  a.varProdId === b.varProdId &&
  a.serviceId === b.serviceId &&
  a.unitId === b.unitId;

/**
 * May this existing line absorb the incoming one?
 *
 * A weighing-scale label carries its own weight, so two packages of tomatoes are
 * two different quantities and must never collapse into one line.
 *
 * `separateLines` is the outlet's "Add each scan as a separate line" switch
 * (outlet.separateCartLines, read through hooks/useSeparateCartLines). When it is on,
 * nothing ever absorbs anything — not a repeat scan, not a unit change onto a sibling.
 * Every caller passes it here rather than deciding for itself.
 */
export const canMergeInto = (line, key, { separateLines = false } = {}) =>
  !separateLines && !!line && !line.isScaleItem && sameCartLine(lineKeyOf(line), key);

/** Index of the line the incoming item should merge into, or -1. */
export const findMergeIndex = (sellItems, key, opts) =>
  (sellItems || []).findIndex((x) => canMergeInto(x, key, opts));

/**
 * Folds incoming rows into the cart, one at a time: a row that matches an existing
 * line (same product, variant and unit) adds its qty to it; anything else is
 * appended. The ONE add rule for the document grids — the search box adds one row,
 * the "Select items" dialog many.
 */
export const addRowsToLines = (sellItems, rows, opts) =>
  (rows || []).reduce((items, row) => {
    if (!row) return items;
    const idx = findMergeIndex(items, lineKeyOf(row), opts);
    if (idx < 0) return [...items, row];
    const next = items.slice();
    next[idx] = absorbLine(next[idx], row);
    return next;
  }, sellItems || []);

/**
 * Index of ANOTHER line this one may fold into — the line it would duplicate after a
 * unit change moved it onto a sibling's unit. -1 when it stands alone (or the outlet
 * keeps every line separate).
 */
export const findSiblingIndex = (sellItems, line, opts) =>
  (sellItems || []).findIndex((x) => x.key !== line?.key && canMergeInto(x, lineKeyOf(line), opts));

/**
 * The survivor of a merge: it keeps its own price — it is the line the user priced (the
 * POS cart's rule) — takes the incoming quantity on top of its own, and both notes. ONE
 * formula for addRowsToLines, the grid's unit-change fold and the picker's batch, so a
 * line never merges differently depending on which door it came through.
 */
export const absorbLine = (survivor, incoming) => ({
  ...survivor,
  qty: (parseFloat(survivor?.qty) || 0) + (parseFloat(incoming?.qty) || 0),
  note: mergeLineNotes(survivor?.note, incoming?.note),
});

/** The key a grid line, a cart payload and a "Select items" row all answer to. */
export const pickerKeyOf = (src) => (src?.varProdId ? `v:${src.varProdId}` : `p:${src?.prodId}`);

export const DIS_PERCENT = 1;
export const DIS_AMOUNT = 2;

/**
 * The discount box as money off the whole line — the document grids' rule (it was
 * ItemsGrid's computeDisAmt): PERCENT is a share of qty × price, AMOUNT is per unit, so it
 * is multiplied by the quantity. Shared with the picker's batch so an edit made there
 * re-derives the same figure the grid would.
 */
export const lineDisAmt = (row) => {
  const qty = parseFloat(row?.qty) || 0;
  const price = parseFloat(row?.price) || 0;
  const discount = parseFloat(row?.discount) || 0;
  const type = row?.disType || DIS_PERCENT;
  if (discount <= 0 || qty <= 0) return 0;
  if (type === DIS_PERCENT) return Math.max(0, (qty * price * discount) / 100);
  return Math.max(0, qty * discount);
};

/**
 * Two lines' notes folded into one when the lines merge: both kept, once each, " / "
 * between, cut to the column's 300. The POS cart's rule (CartProdBody's unit merge),
 * now also what the document grids do when a picked row lands on an existing line.
 */
export const mergeLineNotes = (a, b) =>
  [...new Set([a, b].map((s) => (s || "").trim()).filter(Boolean))].join(" / ").slice(0, 300);

/**
 * Stock is held per product, not per unit, so a shortage has to be judged on the
 * combined draw of every line of that product converted to base units: 1 Box (20)
 * plus 10 Pieces is 30 against stock, even though neither line looks short alone.
 *
 * Mirrors the server-side grouping in SalesInvoiceRepo.CheckStockShortagesAsync so
 * the counter and the API agree on what "short" means. Cart lines do not carry a
 * maintained baseQty, so it is derived from the conversion factor here.
 */
/**
 * The units a product may be traded in: its base unit first, then every extra
 * unit configured on the product.
 *
 * `filterFor` — "sale" keeps units flagged isForSale, "purchase" keeps
 * isForPurchase, omitted keeps all. Omitted is the no-filter default because
 * `controls/UnitSelect` has always behaved that way and three inventory screens
 * rely on it; the sales screens pass "sale" explicitly.
 *
 * One definition, because the POS cart dropdown, the unit picker dialog and
 * UnitSelect must offer exactly the same list. If they drift, a user can pick a
 * unit the line cannot then display.
 */
export const sellableUnitOptions = (prod, filterFor) => {
  if (!prod?.baseUnitId || !(prod?.productUnits?.length > 0)) return [];
  return [
    {
      id: prod.baseUnitId,
      label: prod.baseUnit
        ? `${prod.baseUnit.name} (${prod.baseUnit.abbreviation})`
        : "Base",
      conversionFactor: 1,
      customPrice: null,
    },
    ...prod.productUnits
      .filter((pu) => {
        if (filterFor === "sale") return pu.isForSale;
        if (filterFor === "purchase") return pu.isForPurchase;
        return true;
      })
      .map((pu) => ({
        id: pu.unitId || pu.unit?.id,
        label: pu.unit
          ? `${pu.unit.name} (${pu.unit.abbreviation})`
          : pu.unitName || (pu.unitId ? `Unit ${pu.unitId}` : "Unit"),
        conversionFactor: pu.conversionFactor,
        customPrice: pu.customPrice,
        minPrice: pu.minPrice,
      })),
  ];
};

/**
 * Price for one unit. A unit may carry its own price (a Box is rarely exactly
 * twenty times a Piece); otherwise it is the base price times the factor. Same
 * rule the backend applies in ProductRepo when a unit barcode is scanned.
 */
export const unitPriceFor = (option, basePrice) =>
  parseFloat(option?.customPrice) > 0
    ? parseFloat(option.customPrice)
    : (parseFloat(basePrice) || 0) * (parseFloat(option?.conversionFactor) || 1);

export const productStockKey = (line) =>
  `${line?.prodType ?? ""}:${line?.prodId ?? ""}:${line?.varProdId ?? ""}`;

export const baseQtyByProduct = (sellItems) => {
  const totals = new Map();
  (sellItems || []).forEach((line) => {
    if (!line || line.prodType === PROD_TYPE_SERVICE) return;
    // Free units draw stock exactly like paid ones — a line selling 20 and giving
    // 2 away takes 22 off the shelf. Counting only the paid half would let the
    // cart pass a shortage check it should fail, by the size of the giveaway.
    const qty = (parseFloat(line.qty) || 0) + (parseFloat(line.freeQty) || 0);
    if (qty <= 0) return;
    const factor = parseFloat(line.conversionFactor) || 1;
    const key = productStockKey(line);
    totals.set(key, (totals.get(key) || 0) + qty * factor);
  });
  return totals;
};

/**
 * A cart line as it goes on the wire: every nested object blanked, FK ids kept.
 *
 * A cart re-hydrated from a saved quotation or Sell/GetById still carries the full
 * prod / unit / service graph each line was loaded with. Posted back, two lines in
 * the same unit became two copies of one Unit and the server refused the save
 * ("The instance of entity type 'Unit' cannot be tracked…"). Every endpoint that
 * takes cart lines — sale, quotation, park — goes through this one list; keep it
 * in step with the Includes in SellRepo.ProgGetDetails and QuoteSaleRepo.GetById.
 */
export const stripLineNavs = (line) => ({
  ...line,
  prod: "",
  varProd: "",
  unit: "",
  service: "",
  employee: "",
  serviceBay: "",
  customerVehicle: "",
  serviceDiscountType: "",
});

/**
 * A line given away whole: free units, nothing paid. Allowed only where the branch has
 * "Allow fully free items" (outlet.allowFreeOnlyItems) — the server's FreeGoodsPolicy
 * refuses it everywhere else, so the cart and the receipts ask this one question.
 */
export const isFreeOnlyLine = (it) =>
  (parseFloat(it?.freeQty) || 0) > 0 && (parseFloat(it?.qty) || 0) <= 0;

/**
 * What a cart gives away: free units and their value at SELLING price — the same
 * basis the server's MaxFreeItemPercent cap uses, so the figure on screen is the one
 * that would be refused. Shared by the POS cart and the Direct Invoice footer.
 */
export const freeGoodsOf = (sellItems) =>
  (sellItems || []).reduce(
    (acc, it) => {
      const free = parseFloat(it?.freeQty) || 0;
      if (free <= 0) return acc;
      return {
        qty: acc.qty + free,
        value: acc.value + free * (parseFloat(it.price) || 0),
      };
    },
    { qty: 0, value: 0 }
  );

/**
 * Units of the base unit one of this line's units holds — 12 for a Box of 12, 1 for a
 * line in the base unit or with no unit at all.
 */
export const lineUnitFactor = (line) => parseFloat(line?.conversionFactor) || 1;

/**
 * The line's minimum selling price and cost, in the unit it is being sold in.
 *
 * MinRtlPrice and SplrPrice are stored per BASE unit, while a line's price is for its own
 * unit, so a Box of 12 must clear twelve times the piece minimum — UNLESS the unit carries
 * its own minimum (product_unit.MinPrice), which is the whole Box's floor and is used as
 * it stands. Never for a variant line, which keeps its own minimum × factor.
 *
 * The ONE floor rule, in three places that must agree: this, SellRepo
 * .ValidateSaleMoneyAuthorityAsync (the server, which decides) and the desktop till's
 * ProductLookup.Build. Change all three together.
 */
const ownUnitMinPrice = (line) => {
  if (line?.varProd || line?.varProdId) return 0;
  const unitId = Number(line?.unitId);
  if (!unitId || unitId === Number(line?.prod?.baseUnitId)) return 0;
  const pu = (line?.prod?.productUnits || []).find(
    (u) => Number(u.unitId || u.unit?.id) === unitId
  );
  return parseFloat(pu?.minPrice) || 0;
};

export const lineMinPrice = (line) => {
  const own = ownUnitMinPrice(line);
  if (own > 0) return own;
  return (
    (parseFloat(line?.varProd?.minRtlPrice || line?.prod?.minRtlPrice || 0) || 0) *
    lineUnitFactor(line)
  );
};

/**
 * The largest discount that keeps `listPrice` at or above `minPrice`, in the discount
 * box's own mode (1 = percent, 2 = amount). A discount that would cross the floor is
 * CAPPED to this rather than refused (owner, 2026-10-27): the cashier sees the most they
 * may give instead of a dialog.
 *
 * Percent is rounded DOWN to 2 dp, so the price lands on or just above the floor and
 * never a fil under it — SellRepo would refuse that. The desktop till's
 * LineEdits.MaxDiscountAboveFloor is the same rule; change both together.
 */
export const maxDiscountAboveFloor = (listPrice, minPrice, type, dp = 3) => {
  const list = parseFloat(listPrice) || 0;
  const min = parseFloat(minPrice) || 0;
  if (list <= 0 || min >= list) return 0;
  // The 1e-9 only absorbs float noise (2/5 × 10000 = 3999.9999…), never a real fil.
  if (type === 1) return Math.floor(((list - min) / list) * 10000 + 1e-9) / 100;
  const f = Math.pow(10, dp);
  return Math.floor((list - min) * f + 1e-9) / f;
};

/**
 * The ONE below-minimum decision for a GROSS-entry price box (the document grids and
 * the "Select items" dialog): null when the line is on or above its floor — or open
 * price, which the branch decides on save — otherwise the patch that puts it back on
 * the floor and what to tell the user. The owner's rule (2026-10-27): a price under
 * the minimum is LIFTED to it, a discount that would cross it is CAPPED, never refused.
 *
 *   { kind: "raised", min, patch: { price: min, discount: "" } }
 *   { kind: "capped", min, type, capped, patch: { discount: capped || "" } }
 *
 * The caller applies the patch and shows the toast; the decision lives here so the
 * grid and the dialog cannot drift. The POS cart keeps NET prices and asks the same
 * floor through lineMinPrice / maxDiscountAboveFloor in its own blur handlers.
 */
export const snapLineToMinPrice = (line, dp = 3) => {
  const prod = line?.prod || line?.varProd?.product;
  const min = lineMinPrice(line);
  if (!(min > 0) || prod?.priceType === 3) return null;
  if (grossLineNetPrice(line, dp) >= min - 0.0005) return null;
  const gross = parseFloat(line?.price) || 0;
  if (gross < min - 0.0005) return { kind: "raised", min, patch: { price: min, discount: "" } };
  const type = line?.disType || 1;
  const capped = maxDiscountAboveFloor(gross, min, type, dp);
  return { kind: "capped", min, type, capped, patch: { discount: capped || "" } };
};

/**
 * The line's catalogue price in the unit it is sold in — what a discount on it is
 * measured from. `priceCategoryId` picks a Dynamic Price row instead of the retail price.
 *
 * The ONE list-price rule for the POS cart: the header's strike-through, the price and
 * discount boxes, a qty change and a unit merge all ask this. Each used to read the
 * base-unit rtlPrice on its own, so a Box line struck the piece price, and a qty change
 * zeroed its DisAmt — which the server posts to the GL and counts against the discount cap.
 * Priced through unitPriceFor, like the tile and the unit dropdown; a line re-hydrated
 * without its productUnits falls back to base × the line's own factor.
 */
export const lineListPrice = (line, priceCategoryId = null) => {
  let base = parseFloat(line?.varProd?.rtlPrice || line?.prod?.rtlPrice || 0) || 0;
  if (priceCategoryId) {
    const rp = (line?.prod?.retailPrices || []).find(
      (r) => r.priceCategoryId === priceCategoryId
    );
    if (rp) base = parseFloat(rp.price || 0) || 0;
  }
  const unitId = Number(line?.unitId || line?.prod?.baseUnitId);
  const option = sellableUnitOptions(line?.prod).find((u) => Number(u.id) === unitId);
  return option ? unitPriceFor(option, base) : base * lineUnitFactor(line);
};

export const lineCostPrice = (line) =>
  (parseFloat(line?.varProd?.splrPrice || line?.prod?.splrPrice || 0) || 0) *
  lineUnitFactor(line);

/**
 * GROSS-entry grids (Direct Invoice) and the server's NET price.
 *
 * SellItem.Price is NET of the line discount — the server books Σ Qty × Price and treats
 * DisAmt as information. The POS cart already keeps a net price. The Direct Invoice grid
 * lets a person type the list price and a discount beside it, so its row.price is GROSS;
 * it posted that unchanged, and the server booked every line discount as if it had not
 * been given (prestigef CRI-137: 20,941 of discounts left on the customer's account).
 *
 * The net price is rounded to the currency's decimals, as the POS does, and EVERY figure
 * the grid shows comes from it — so the screen's total is the server's to the fil.
 * DisAmt is then restated as qty × (gross − net), which is exactly what was taken off.
 */
const roundTo = (v, dp) => {
  const f = Math.pow(10, dp);
  return Math.round(v * f) / f;
};

export const grossLineNetPrice = (line, dp = 3) => {
  const qty = parseFloat(line?.qty) || 0;
  const gross = parseFloat(line?.price) || 0;
  const disAmt = parseFloat(line?.disAmt) || 0;
  if (qty <= 0 || disAmt <= 0) return gross;
  return roundTo(Math.max(0, gross - disAmt / qty), dp);
};

/** A gross grid row as it goes on the wire: net price, DisAmt restated to match. */
export const grossLineToWire = (line, dp = 3) => {
  const qty = parseFloat(line?.qty) || 0;
  const gross = parseFloat(line?.price) || 0;
  const net = grossLineNetPrice(line, dp);
  return { ...line, price: net, disAmt: roundTo(Math.max(0, (gross - net) * qty), dp) };
};

/**
 * A stored line (net price, as every sale and Sales Order line is) back into a gross
 * grid row: the price the discount was taken off. Exact for anything grossLineToWire
 * wrote, because that DisAmt is precisely qty × (gross − net).
 */
export const netLineToGross = (line, dp = 3) => {
  const qty = parseFloat(line?.qty) || 0;
  const net = parseFloat(line?.price) || 0;
  const disAmt = parseFloat(line?.disAmt) || 0;
  if (qty <= 0 || disAmt <= 0) return line;
  return { ...line, price: roundTo(net + disAmt / qty, dp) };
};

/**
 * What the "Select items" dialog hands back, applied to the document in ONE pass:
 *   removedKeys — grid lines unticked in the dialog: gone;
 *   updates     — [{ lineKey, patch }] for lines that were already on the document and
 *                 were edited there (qty, unit, price, note, discount);
 *   added       — fresh rows (toCartRow shape) for the new ticks.
 * Removals go first so a removed line can neither be edited nor absorb anything. An
 * update re-derives disAmt, is held to the floor once more (the dialog snapped against
 * the page it loaded, which may be older), and — when its unit change landed on a line
 * already in that unit — folds into it by the grid's own unit-change rule. New rows go
 * last through addRowsToLines, so a new Box pick can still land on a line an update just
 * moved to Box. Pure: the toasts belong to the dialog and the button.
 */
export const applyPickerBatch = (
  sellItems,
  { added = [], updates = [], removedKeys = [] } = {},
  { separateLines = false, dp = 3 } = {}
) => {
  const removed = new Set(removedKeys);
  let items = (sellItems || []).filter((l) => !removed.has(l.key));

  for (const { lineKey, patch } of updates) {
    const idx = items.findIndex((l) => l.key === lineKey);
    if (idx < 0) continue;
    let next = { ...items[idx], ...patch };
    // A billed sales-order line is capped at what the order still owes (the grid's rule).
    if (next.maxQty != null && next.maxQty !== "" && parseFloat(next.qty) > parseFloat(next.maxQty))
      next.qty = next.maxQty;
    next.disAmt = lineDisAmt(next);
    const snap = snapLineToMinPrice(next, dp);
    if (snap) {
      next = { ...next, ...snap.patch };
      next.disAmt = lineDisAmt(next);
    }
    const sib = findSiblingIndex(items, next, { separateLines });
    if (sib >= 0) {
      const merged = absorbLine(items[sib], next);
      items = items
        .map((l, i) => (i === sib ? { ...merged, disAmt: lineDisAmt(merged) } : l))
        .filter((_, i) => i !== idx);
    } else {
      items = items.map((l, i) => (i === idx ? next : l));
    }
  }

  return addRowsToLines(items, added, { separateLines });
};
